import { createSelector } from "reselect";
import { CommerceToolsApi } from "@grandvisionhq/commercetools";
import { Selectors } from "@grandvisionhq/configurator";
import { statusSuccess } from "@grandvisionhq/state";
import { getLocalizedValue, isDefined } from "@grandvisionhq/utils";
import { getBundleProductTitle } from "../../../../utils/utils/get-bundle-product-title.js";
import { getLensProductVariantName } from "../../../../utils/utils/get-lens-product-variant-name.js";
import { isNewPrescriptionAndUpload } from "../../../../utils/utils/is-new-prescription-upload.js";
import { convertToLensTypeInfo, convertToLensAddOnInfo } from "../../../state/helpers.js";
import { getLensTypes, lookupParentLensType, getPackages, lookupParentAddOn } from "../../../products/state/products/selectors.internal.js";
import "../../../add-to-cart/state/add-to-cart/selectors.js";
import "../../../bundles/state/bundles/selectors.js";
import "../../../bundles/state/bundles/reducer.js";
import { getBundlePrice, getPrescription, getSelectedLensType, getSelectedPackage, getSelectedAddOn, hasVisitedStepAddOn, getPrescriptionType, hasVisitedStepPrescription } from "../../../configurator/state/configurator/selectors.js";
import "../../../configurator/state/configurator/reducer.js";
import "../../../products/state/products/selectors.js";
import "../../../products/state/products/reducer.js";
import { configuratorIdProp, stateProp } from "../../../state/selectors.internal.js";
const stepMapping = {
  lensType: {
    name: "lens type",
    id: "100"
  },
  lensTypeVariant: {
    name: "lens type variant",
    id: "150"
  },
  prescription: {
    name: "prescription",
    id: "200"
  },
  prescriptionForm: {
    name: "prescription",
    id: "210"
  },
  prescriptionUpload: {
    name: "prescription",
    id: "210"
  },
  prescriptionValidation: {
    name: "prescription validation",
    id: "250"
  },
  lensPackage: {
    name: "lens package",
    id: "300"
  },
  lensAddOn: {
    name: "add-ons",
    id: "400"
  },
  lensAddOnVariant: {
    name: "add-ons-variant",
    id: "450"
  },
  review: {
    name: "review",
    id: "500"
  }
};
const getStepData = createSelector(
  configuratorIdProp,
  stateProp,
  (id, s) => {
    const currentStep = Selectors.getCurrentStep(s, { id });
    return currentStep ? stepMapping[currentStep.name] : void 0;
  }
);
const getPrice = createSelector(
  getBundlePrice,
  (bundlePrice) => {
    if (!bundlePrice) {
      console.warn(
        `Need bundle price to log data layer event. Received "${JSON.stringify(bundlePrice)}"`
      );
      return;
    }
    const { discountedPrice, originalPrice } = bundlePrice;
    return discountedPrice ? (discountedPrice / 100).toFixed(2) : (originalPrice / 100).toFixed(2);
  }
);
const formatPrescriptionValue = (value) => typeof value === "number" && !isNaN(value) ? value.toFixed(2) : "n/a";
const reduceParameter = (values) => (info, { name }) => ({
  ...info,
  [name]: formatPrescriptionValue(values[name])
});
const getLensParameters = createSelector(
  getPrescription,
  (pre) => {
    if (!statusSuccess(pre)) {
      return;
    }
    const { parameters, left, right } = pre.result;
    return {
      OS: parameters.reduce(reduceParameter(left), {}),
      OD: parameters.reduce(reduceParameter(right), {})
    };
  }
);
const getSelectedLensNames = createSelector(
  getLensTypes,
  getSelectedLensType,
  (s) => s,
  (lensTypes, selectedLensType, state) => {
    if (!lensTypes || !selectedLensType) {
      return;
    }
    const lensTypeProduct = lookupParentLensType(state, {
      lensTypeSku: selectedLensType.sku
    });
    if (!lensTypeProduct) {
      return;
    }
    const lensTypeInfo = convertToLensTypeInfo(lensTypeProduct, selectedLensType.sku, "en");
    return {
      productName: lensTypeInfo.title,
      variantName: getLensProductVariantName(lensTypeInfo)
    };
  }
);
const getSelectedPackageName = createSelector(
  getPackages,
  getSelectedPackage,
  (packages, selectedPackage) => {
    if (!packages || !selectedPackage) {
      return;
    }
    const packageProductData = packages[selectedPackage.sku];
    if (!packageProductData) {
      return;
    }
    return getLocalizedValue(packageProductData.name, "en");
  }
);
const getSelectedAddOnName = createSelector(
  getSelectedAddOn,
  (s) => s,
  (selectedAddOn, state) => {
    if (!selectedAddOn) {
      return "no add-on";
    }
    const addOnProduct = lookupParentAddOn(state, {
      addOnSku: selectedAddOn.sku
    });
    if (!addOnProduct) {
      return;
    }
    const addOnInfo = convertToLensAddOnInfo(addOnProduct, selectedAddOn.sku, "en");
    return getBundleProductTitle(addOnInfo);
  }
);
const getProductConfig = createSelector(
  getSelectedLensNames,
  getSelectedPackageName,
  getSelectedAddOnName,
  getPrice,
  getLensParameters,
  hasVisitedStepAddOn,
  (lensNames, packageName, addOnName, price, lens, hasVisitedStepAddOn2) => {
    return {
      ...lensNames?.productName ? { lensType: lensNames.productName.toLowerCase() } : {},
      /**
       * Property naming discussed with Helena Heno on 2021/06/09.
       */
      ...lensNames?.variantName ? { lensVariant: lensNames.variantName.toLowerCase() } : {},
      ...packageName ? { lensPackage: packageName.toLowerCase() } : {},
      ...hasVisitedStepAddOn2 && addOnName ? { "add-ons": addOnName.toLowerCase() } : {},
      price: price ?? "0",
      ...lens ? { lens } : {}
    };
  }
);
const getConfigFlow = createSelector(
  getStepData,
  getPrescriptionType,
  hasVisitedStepPrescription,
  (stepData, prescriptionType, hasVisitedStepPrescription2) => {
    if (!stepData) {
      return void 0;
    }
    const rxInputMethodMapping = {
      NewPrescription: "manual",
      EmailPrescription: "email",
      ExistingCustomer: "customer",
      PrescriptionFile: "upload",
      NewPrescriptionAndUpload: "manual",
      "book-appointment": "book-appointment"
    };
    if (CommerceToolsApi.isPrescriptionFile(prescriptionType) || isNewPrescriptionAndUpload(prescriptionType)) {
      return {
        version: "1.0",
        step: stepData
      };
    }
    return {
      version: "1.0",
      step: stepData,
      ...hasVisitedStepPrescription2 && isDefined(prescriptionType) ? { RxInputMethod: rxInputMethodMapping[prescriptionType] } : {}
    };
  }
);
const getUser = createSelector(
  getPrescriptionType,
  (prescriptionType) => {
    if (CommerceToolsApi.isPrescriptionFile(prescriptionType) || isNewPrescriptionAndUpload(prescriptionType)) {
      return;
    }
    return {
      custStatus: CommerceToolsApi.isExistingCustomer(prescriptionType) ? "customer" : "prob noncustomer"
    };
  }
);
export {
  getConfigFlow,
  getLensParameters,
  getPrice,
  getProductConfig,
  getSelectedAddOnName,
  getSelectedLensNames,
  getSelectedPackageName,
  getStepData,
  getUser
};
//# sourceMappingURL=selectors.internal.js.map
